import Breadcrumb from "components/Common/Breadcrumb"
import { useMemo, useState } from "react"
import { FaEdit, FaEye, FaPlus, FaSave, FaWhatsapp } from "react-icons/fa"
import { MdCancel, MdDelete } from "react-icons/md"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import React from "react"
import Swal from "sweetalert2"
import { getAllTemplatesResponse } from "utils/dummy_data"

const CATEGORIES = ["MARKETING", "AUTHENTICATION", "UTILITY"]
const STATUSES = ["APPROVED", "PENDING", "REJECTED"]

const EMPTY_FORM = {
  name: "",
  language: "en",
  category: "MARKETING",
  status: "PENDING",
  bodyText: "",
  bodyExample: [""],
  buttons: [],
}

const getBodyComponent = template =>
  template?.components?.find(c => c.type === "BODY")

const getButtonsComponent = template =>
  template?.components?.find(c => c.type === "BUTTONS")

const getCategoryColor = category => {
  switch (category) {
    case "MARKETING":
      return "primary"
    case "AUTHENTICATION":
      return "dark"
    case "UTILITY":
      return "info"
    default:
      return "secondary"
  }
}

const getStatusColor = status => {
  switch (status) {
    case "APPROVED":
      return "success"
    case "PENDING":
      return "warning"
    case "REJECTED":
      return "danger"
    default:
      return "secondary"
  }
}

const countPlaceholders = text => {
  const matches = (text || "").match(/{{\d+}}/g)
  return matches ? new Set(matches).size : 0
}

const templateToForm = template => {
  const body = getBodyComponent(template)
  const buttonsComp = getButtonsComponent(template)
  return {
    id: template.id,
    name: template.name || "",
    language: template.language || "en",
    category: template.category || "MARKETING",
    status: template.status || "PENDING",
    allow_category_change: template.allow_category_change || false,
    bodyText: body?.text || "",
    bodyExample: body?.example?.body_text?.[0]
      ? [...body.example.body_text[0]]
      : [""],
    buttons: buttonsComp?.buttons
      ? buttonsComp.buttons.map(b => ({
          type: b.type || "URL",
          text: b.text || "",
          url: b.url || "",
          example: b.example || [],
        }))
      : [],
  }
}

const formToTemplate = (form, existing) => {
  const components = [
    {
      type: "BODY",
      text: form.bodyText.trim(),
      example: {
        body_text: [
          (form.bodyExample || []).map(v => (v || "").trim()),
        ],
      },
    },
  ]
  const validButtons = (form.buttons || []).filter(b => b.text?.trim())
  if (validButtons.length > 0) {
    components.push({
      type: "BUTTONS",
      buttons: validButtons.map(b => {
        const btn = { type: b.type, text: b.text.trim() }
        if (b.url?.trim()) btn.url = b.url.trim()
        // Keep OTP-style example when the url has a variable
        if (b.url?.includes("{{1}}")) {
          btn.example = [
            b.url.replace("{{1}}", "123456"),
          ]
        } else if (b.example?.length) {
          btn.example = b.example
        }
        return btn
      }),
    })
  }
  return {
    ...(existing || {}),
    id: form.id || existing?.id || String(Date.now()),
    name: form.name.trim(),
    language: form.language,
    category: form.category,
    status: form.status,
    allow_category_change: false,
    components,
  }
}

const WATemplates = () => {
  document.title = "WA Templates | Vintage Infra Equipment Sol PVT LTD"
  const [data, setData] = useState(getAllTemplatesResponse?.data || [])
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")
  const [categoryFilter, setCategoryFilter] = useState("")

  const [currentPage, setCurrentPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})

  const [viewTemplate, setViewTemplate] = useState(null)

  // TODO: gate with backend permission id when available (showing openly for now)
  // const { checkAccess } = usePermissionData()

  const filteredData = useMemo(() => {
    const search = searchString.toLowerCase()
    return data.filter(item => {
      const matchesSearch =
        !search ||
        item?.name?.toLowerCase().includes(search) ||
        item?.language?.toLowerCase().includes(search) ||
        item?.category?.toLowerCase().includes(search) ||
        item?.status?.toLowerCase().includes(search) ||
        getBodyComponent(item)?.text?.toLowerCase().includes(search)
      const matchesCategory =
        !categoryFilter || item.category === categoryFilter
      return matchesSearch && matchesCategory
    })
  }, [data, searchString, categoryFilter])

  const totalPages = Math.ceil(filteredData.length / pageSize) || 1
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(() => {
    const end = start + pageSize
    return filteredData.slice(start, end)
  }, [filteredData, currentPage, pageSize, start])

  const resetPage = () => setCurrentPage(1)

  const openAddModal = () => {
    setSelectedTemplate({ ...EMPTY_FORM, bodyExample: [""] , buttons: [] })
    setErrors({})
    setModalOpen(true)
  }

  const openEditModal = item => {
    setSelectedTemplate(templateToForm(item))
    setErrors({})
    setModalOpen(true)
  }

  const validate = () => {
    const newErrors = {}
    if (!selectedTemplate?.name?.trim()) {
      newErrors.name = "Template name is required"
    } else if (!/^[a-z0-9_]+$/.test(selectedTemplate.name.trim())) {
      newErrors.name = "Only lowercase letters, numbers and _ allowed"
    }
    if (!selectedTemplate?.category) {
      newErrors.category = "Category is required"
    }
    if (!selectedTemplate?.bodyText?.trim()) {
      newErrors.bodyText = "Body text is required"
    }
    const placeholders = countPlaceholders(selectedTemplate?.bodyText)
    const filledExamples = (selectedTemplate?.bodyExample || []).filter(v =>
      v?.trim()
    ).length
    if (placeholders > 0 && filledExamples < placeholders) {
      newErrors.bodyExample = `Body has ${placeholders} variable(s), provide at least ${placeholders} example value(s)`
    }
    ;(selectedTemplate?.buttons || []).forEach((b, i) => {
      if (b.text?.trim() && b.type === "URL" && !b.url?.trim()) {
        newErrors[`button_url_${i}`] = "Button URL is required"
      }
    })
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const syncExampleLength = bodyText => {
    const n = countPlaceholders(bodyText)
    setSelectedTemplate(prev => {
      const curr = [...(prev.bodyExample || [])]
      while (curr.length < Math.max(n, 1)) curr.push("")
      return { ...prev, bodyText, bodyExample: curr.slice(0, Math.max(n, 1)) }
    })
  }

  // Local CRUD only (create / update / delete APIs to be wired later)
  const handleSubmit = () => {
    if (!validate()) return

    if (selectedTemplate?.id) {
      const existing = data.find(d => String(d.id) === String(selectedTemplate.id))
      const updated = formToTemplate(selectedTemplate, existing)
      setData(prev =>
        prev.map(item =>
          String(item.id) === String(selectedTemplate.id) ? updated : item
        )
      )
      Swal.fire({
        title: "Template Updated Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    } else {
      // Prevent duplicate name
      const duplicate = data.some(
        d =>
          d.name?.toLowerCase() ===
          selectedTemplate.name.trim().toLowerCase()
      )
      if (duplicate) {
        setErrors(prev => ({
          ...prev,
          name: "A template with this name already exists",
        }))
        return
      }
      const created = formToTemplate(selectedTemplate, null)
      setData(prev => [created, ...prev])
      Swal.fire({
        title: "Template Created Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    }
    setModalOpen(false)
    setSelectedTemplate(EMPTY_FORM)
    setCurrentPage(1)
    // TODO: replace with FetchPostData / FetchPutData "wa-templates" when API is ready
  }

  const handleDelete = item => {
    Swal.fire({
      title: `Are you sure you want to delete "${item?.name}"?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.isConfirmed) {
        setData(prev => prev.filter(d => String(d.id) !== String(item.id)))
        Swal.fire({
          title: "Template Deleted Successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        })
        // TODO: replace with FetchDeleteData "wa-templates" when API is ready
      }
    })
  }

  return (
    <div className="page-content">
      <Breadcrumb title="Dashboard" breadcrumbItem="WA Templates" />

      <Card className="shadow-lg">
        <CardBody>
          {/* Top controls */}
          <div className="d-flex justify-content-between align-items-center gap-2 mb-3 flex-nowrap">
            <div className="d-flex gap-2 align-items-center flex-nowrap flex-grow-1">
              <Input
                type="select"
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value)), setCurrentPage(1)
                }}
                className="rounded-4 pe-5 flex-shrink-0"
                style={{ width: "90px" }}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>

              <Input
                type="select"
                value={categoryFilter}
                onChange={e => {
                  setCategoryFilter(e.target.value)
                  resetPage()
                }}
                className="rounded-4 pe-5 flex-shrink-0"
                style={{ width: "180px" }}
              >
                <option value="">All Categories</option>
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Input>

              <Input
                className="rounded-4 flex-grow-1"
                style={{ maxWidth: "320px" }}
                placeholder="Search Templates"
                value={searchString}
                onChange={e => {
                  setSearchString(e.target.value)
                  resetPage()
                }}
              />
            </div>
            <Button
              className="btn-blue rounded-4 px-3 flex-shrink-0 text-nowrap"
              onClick={openAddModal}
            >
              <FaPlus className="me-2" />
              WA Template
            </Button>
          </div>

          {/* Table */}
          <Table bordered responsive className="align-middle">
            <thead className="table-dark">
              <tr>
                <th className="text-center" style={{ width: "70px" }}>
                  #
                </th>
                <th style={{ minWidth: "220px" }}>Template Name</th>
                <th className="text-center" style={{ width: "190px" }}>
                  Template ID
                </th>
                <th className="text-center" style={{ width: "170px" }}>
                  Category
                </th>
                <th className="text-center" style={{ width: "150px" }}>
                  Status
                </th>
                <th className="text-center" style={{ width: "170px" }}>
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    No WA Templates found
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => {
                  return (
                    <tr key={item.id}>
                      <td className="text-center">{start + index + 1}</td>
                      <td>
                        <span className="fw-bold">{item.name}</span>
                      </td>
                      <td
                        className="text-center text-muted small"
                        style={{ wordBreak: "break-all" }}
                      >
                        {item.id}
                      </td>
                      <td className="text-center">
                        <Badge
                          pill
                          color={getCategoryColor(item.category)}
                          style={{ fontSize: "0.9rem", padding: "0.45em 1em" }}
                        >
                          {item.category}
                        </Badge>
                      </td>
                      <td className="text-center">
                        <Badge
                          pill
                          color={getStatusColor(item.status)}
                          style={{ fontSize: "0.9rem", padding: "0.45em 1em" }}
                        >
                          {item.status}
                        </Badge>
                      </td>
                      <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                        <Button
                          className="bg-info text-white me-2 border-0 shadow-none"
                          title="View template"
                          onClick={() => setViewTemplate(item)}
                        >
                          <FaEye size={16} />
                        </Button>
                        <Button
                          className="bg-success text-white me-2 border-0 shadow-none"
                          title="Edit template"
                          onClick={() => openEditModal(item)}
                        >
                          <FaEdit size={16} />
                        </Button>
                        <Button
                          className="bg-danger text-white border-0 shadow-none"
                          title="Delete template"
                          onClick={() => handleDelete(item)}
                        >
                          <MdDelete size={18} />
                        </Button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </Table>
          <nav
            aria-label="Page navigation example"
            className="d-flex justify-content-between mt-3"
          >
            <h5>
              Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {filteredData.length} entries
            </h5>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                )
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>

      {/* View modal */}
      <Modal
        isOpen={!!viewTemplate}
        toggle={() => setViewTemplate(null)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setViewTemplate(null)}>
          <span className="d-flex align-items-center gap-2">
            <FaWhatsapp size={20} className="text-success" />
            {viewTemplate?.name}
          </span>
        </ModalHeader>
        <ModalBody>
          {viewTemplate && (
            <>
              {viewTemplate.components
                ?.filter(comp => comp.type === "BODY")
                ?.map((comp, i) => (
                  <Row key={i}>
                    <Col md={8} className="mb-3">
                      <div className="border rounded-4 p-3 h-100 bg-light">
                        <div className="fw-bold mb-2 small text-muted">BODY</div>
                        <div style={{ whiteSpace: "pre-wrap" }}>
                          {comp.text}
                        </div>
                      </div>
                    </Col>
                    {comp.example?.body_text?.[0] && (
                      <Col md={4} className="mb-3">
                        <div className="border rounded-4 p-3 h-100 bg-white">
                          <div className="fw-bold mb-2 small text-muted">
                            EXAMPLE
                          </div>
                          {comp.example.body_text[0].map((val, idx) => (
                            <div
                              key={idx}
                              className="border rounded-3 px-2 py-1 mb-2 small bg-light"
                            >
                              <span className="fw-bold text-muted">
                                {"{{" + (idx + 1) + "}}"}:{" "}
                              </span>
                              {val}
                            </div>
                          ))}
                        </div>
                      </Col>
                    )}
                  </Row>
                ))}
            </>
          )}
        </ModalBody>
      </Modal>

      {/* Add / Edit modal */}
      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setModalOpen(false)}>
          {selectedTemplate?.id ? "Edit" : "Add"} WA Template
        </ModalHeader>
        <ModalBody>
          {!selectedTemplate?.id && (
            <Row>
              <Col md={8} className="mb-3">
                <Label>
                  Template Name <span className="text-danger">*</span>
                </Label>
                <Input
                  placeholder="e.g. job_notification"
                  value={selectedTemplate?.name || ""}
                  invalid={!!errors.name}
                  onChange={e =>
                    setSelectedTemplate(prev => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
                {errors.name && (
                  <FormFeedback className="d-block">{errors.name}</FormFeedback>
                )}
              </Col>
              <Col md={4} className="mb-3">
                <Label>
                  Category <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  value={selectedTemplate?.category || "MARKETING"}
                  invalid={!!errors.category}
                  onChange={e =>
                    setSelectedTemplate(prev => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                >
                  {CATEGORIES.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Input>
                {errors.category && (
                  <FormFeedback className="d-block">
                    {errors.category}
                  </FormFeedback>
                )}
              </Col>
            </Row>
          )}

          <Row>
            <Col md={8} className="mb-3">
              <Label>
                Body Text <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                rows={15}
                placeholder="Hi {{1}}, your code is {{2}}..."
                value={selectedTemplate?.bodyText || ""}
                invalid={!!errors.bodyText}
                onChange={e => syncExampleLength(e.target.value)}
                style={{ whiteSpace: "pre-wrap", minHeight: "340px" }}
              />
              {errors.bodyText ? (
                <FormFeedback className="d-block">
                  {errors.bodyText}
                </FormFeedback>
              ) : (
                <small className="text-muted">
                  Use {"{{1}}, {{2}}..."} for variables. Detected:{" "}
                  {countPlaceholders(selectedTemplate?.bodyText)} variable(s)
                </small>
              )}
            </Col>

            <Col md={4} className="mb-3">
              <Label>Body Example Values</Label>
              {(selectedTemplate?.bodyExample || [""]).map((val, i) => (
                <Input
                  key={i}
                  className="mb-2"
                  placeholder={`Value for {{${i + 1}}}`}
                  value={val || ""}
                  invalid={!!errors.bodyExample && i === 0}
                  onChange={e =>
                    setSelectedTemplate(prev => {
                      const next = [...(prev.bodyExample || [])]
                      next[i] = e.target.value
                      return { ...prev, bodyExample: next }
                    })
                  }
                />
              ))}
              {errors.bodyExample && (
                <FormFeedback className="d-block">
                  {errors.bodyExample}
                </FormFeedback>
              )}
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button className="btn-blue" onClick={handleSubmit}>
            <FaSave className="me-1" /> Save
          </Button>
          <Button className="btn-danger" onClick={() => setModalOpen(false)}>
            <MdCancel className="me-1" /> Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default WATemplates
